<template>
  <section
    class="cycle-task-detail-dependencies"
    data-testid="cycle-task-detail-dependencies"
  >
    <div
      class="cycle-task-detail-dependencies__header"
      data-testid="cycle-task-detail-dependencies__header"
    >
      <h2 class="cycle-task-detail-dependencies__title">Dependencies</h2>
    </div>

    <div
      class="cycle-task-detail-dependencies__viewport"
      data-testid="cycle-task-detail-dependencies__viewport"
    >
      <div
        v-if="error"
        class="cycle-task-detail-dependencies__empty"
        data-testid="summary-tab__dependencies-error"
      >
        <p class="cycle-task-detail-dependencies__empty-title">
          {{ loadErrorTitle }}
        </p>
        <p class="cycle-task-detail-dependencies__empty-body">
          {{ loadErrorDescription }}
        </p>
        <button
          type="button"
          class="cycle-task-detail-dependencies__retry"
          data-testid="summary-tab__dependencies-retry"
          @click="$emit('retry')"
        >
          {{ loadErrorRetry }}
        </button>
      </div>
      <div
        v-else-if="loading"
        class="text-center pa-6"
        data-testid="summary-tab__dependencies-loading"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="32"
          data-testid="summary-tab__dependencies-loading-spinner"
        />
        <div class="mt-2" data-testid="summary-tab__dependencies-loading-text">Loading dependencies...</div>
      </div>
      <div v-else-if="hasLinks" class="cycle-task-detail-dependencies__body">
        <div
          class="cycle-task-detail-dependencies__group"
          data-testid="cycle-task-detail-dependencies__group--blockedBy"
        >
          <div class="cycle-task-detail-dependencies__group-header">
            <h3 class="cycle-task-detail-dependencies__group-title">
              {{ copy.DETAIL_WAITING_ON }}
            </h3>
            <span
              class="cycle-task-detail-dependencies__count"
              :data-testid="`cycle-task-detail-dependencies__count--blockedBy`"
            >
              {{ blockedBy.length }}
            </span>
          </div>
          <div
            v-if="blockedBy.length"
            class="cycle-task-detail-dependencies__list cycle-task-detail-dependencies__list--waiting"
          >
            <div
              v-for="(row, index) in blockedBy"
              :key="`blockedBy-${rowId(row)}-${index}`"
              class="cycle-task-detail-dependencies__item"
              :data-testid="`cycle-task-detail-dependencies__row--blockedBy-${index}`"
            >
              <div class="cycle-task-detail-dependencies__item-line">
                <span
                  v-if="row.ref"
                  class="cycle-task-detail-dependencies__ref"
                >{{ row.ref }}</span>
                <v-tooltip
                  bottom
                  open-delay="300"
                  content-class="tooltip-bottom tooltip-wrap"
                  class="cycle-task-detail-dependencies__name-tooltip"
                  :disabled="rowLabel(row) === rowTitle(row)"
                >
                  <template #activator="{ on, attrs }">
                    <button
                      v-if="row.canVisit"
                      type="button"
                      class="cycle-task-detail-dependencies__name cycle-task-detail-dependencies__name--link"
                      v-bind="attrs"
                      :data-testid="`cycle-task-detail-dependencies__name--blockedBy-${index}`"
                      v-on="on"
                      @click="onRowClick(row)"
                    >
                      {{ rowLabel(row) }}
                    </button>
                    <span
                      v-else
                      class="cycle-task-detail-dependencies__name cycle-task-detail-dependencies__name--muted"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ rowLabel(row) }}
                    </span>
                  </template>
                  <span>{{ rowTitle(row) }}</span>
                </v-tooltip>
              </div>
              <p class="cycle-task-detail-dependencies__meta">
                {{ metaLine(row) }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="cycle-task-detail-dependencies__group"
          data-testid="cycle-task-detail-dependencies__group--blocking"
        >
          <div class="cycle-task-detail-dependencies__group-header">
            <h3 class="cycle-task-detail-dependencies__group-title">
              {{ copy.DETAIL_BLOCKING }}
            </h3>
            <span
              class="cycle-task-detail-dependencies__count"
              :data-testid="`cycle-task-detail-dependencies__count--blocking`"
            >
              {{ blocking.length }}
            </span>
          </div>
          <div
            v-if="blocking.length"
            class="cycle-task-detail-dependencies__list cycle-task-detail-dependencies__list--blocking"
          >
            <div
              v-for="(row, index) in blocking"
              :key="`blocking-${rowId(row)}-${index}`"
              class="cycle-task-detail-dependencies__item"
              :data-testid="`cycle-task-detail-dependencies__row--blocking-${index}`"
            >
              <div class="cycle-task-detail-dependencies__item-line">
                <span
                  v-if="row.ref"
                  class="cycle-task-detail-dependencies__ref"
                >{{ row.ref }}</span>
                <v-tooltip
                  bottom
                  open-delay="300"
                  content-class="tooltip-bottom tooltip-wrap"
                  class="cycle-task-detail-dependencies__name-tooltip"
                  :disabled="rowLabel(row) === rowTitle(row)"
                >
                  <template #activator="{ on, attrs }">
                    <button
                      v-if="row.canVisit"
                      type="button"
                      class="cycle-task-detail-dependencies__name cycle-task-detail-dependencies__name--link"
                      v-bind="attrs"
                      :data-testid="`cycle-task-detail-dependencies__name--blocking-${index}`"
                      v-on="on"
                      @click="onRowClick(row)"
                    >
                      {{ rowLabel(row) }}
                    </button>
                    <span
                      v-else
                      class="cycle-task-detail-dependencies__name cycle-task-detail-dependencies__name--muted"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ rowLabel(row) }}
                    </span>
                  </template>
                  <span>{{ rowTitle(row) }}</span>
                </v-tooltip>
              </div>
              <p class="cycle-task-detail-dependencies__meta">
                {{ metaLine(row) }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="cycle-task-detail-dependencies__divider"
          role="presentation"
          aria-hidden="true"
        />

        <div
          class="cycle-task-detail-dependencies__group"
          data-testid="cycle-task-detail-dependencies__group--relates"
        >
          <div class="cycle-task-detail-dependencies__group-header">
            <h3 class="cycle-task-detail-dependencies__group-title">
              {{ copy.DETAIL_RELATED }}
            </h3>
            <span
              class="cycle-task-detail-dependencies__count"
              :data-testid="`cycle-task-detail-dependencies__count--relates`"
            >
              {{ relates.length }}
            </span>
          </div>
          <div
            v-if="relates.length"
            class="cycle-task-detail-dependencies__list cycle-task-detail-dependencies__list--related"
          >
            <div
              v-for="(row, index) in relates"
              :key="`relates-${rowId(row)}-${index}`"
              class="cycle-task-detail-dependencies__item"
              :data-testid="`cycle-task-detail-dependencies__row--relates-${index}`"
            >
              <div class="cycle-task-detail-dependencies__item-line">
                <span
                  v-if="row.ref"
                  class="cycle-task-detail-dependencies__ref"
                >{{ row.ref }}</span>
                <v-tooltip
                  bottom
                  open-delay="300"
                  content-class="tooltip-bottom tooltip-wrap"
                  class="cycle-task-detail-dependencies__name-tooltip"
                  :disabled="rowLabel(row) === rowTitle(row)"
                >
                  <template #activator="{ on, attrs }">
                    <button
                      v-if="row.canVisit"
                      type="button"
                      class="cycle-task-detail-dependencies__name cycle-task-detail-dependencies__name--link"
                      v-bind="attrs"
                      :data-testid="`cycle-task-detail-dependencies__name--relates-${index}`"
                      v-on="on"
                      @click="onRowClick(row)"
                    >
                      {{ rowLabel(row) }}
                    </button>
                    <span
                      v-else
                      class="cycle-task-detail-dependencies__name cycle-task-detail-dependencies__name--muted"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ rowLabel(row) }}
                    </span>
                  </template>
                  <span>{{ rowTitle(row) }}</span>
                </v-tooltip>
              </div>
              <p class="cycle-task-detail-dependencies__meta">
                {{ metaLine(row) }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="cycle-task-detail-dependencies__empty"
        data-testid="cycle-task-detail-dependencies__empty"
      >
        <div class="cycle-task-detail-dependencies__empty-icon">
          <img
            src="/img/icons/fbc-icons/DependenciesEmptyLink.svg"
            alt=""
          />
        </div>
        <p class="cycle-task-detail-dependencies__empty-title">
          {{ copy.DETAIL_EMPTY_TITLE }}
        </p>
        <p class="cycle-task-detail-dependencies__empty-body">
          {{ copy.DETAIL_EMPTY_BODY }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import './CycleTaskDetailDependencies.scss';
import { CYCLE_TASK_DEPENDENCY, TASK_DETAIL_EMPTY_STATE } from '../../../../../utils/constants';
import {
  templateIdString,
  truncateDependencyDescription,
} from '../../../../Setup/TaskList/utils/setupDependencyUtils';

export default {
  name: 'CycleTaskDetailDependencies',

  props: {
    blocking: { type: Array, default: () => [] },
    blockedBy: { type: Array, default: () => [] },
    relates: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
  },

  data() {
    return {
      copy: CYCLE_TASK_DEPENDENCY,
      loadErrorTitle: TASK_DETAIL_EMPTY_STATE.LOAD_ERROR.TITLE,
      loadErrorDescription: TASK_DETAIL_EMPTY_STATE.LOAD_ERROR.DESCRIPTION,
      loadErrorRetry: TASK_DETAIL_EMPTY_STATE.LOAD_ERROR.RETRY,
    };
  },

  computed: {
    hasLinks() {
      return this.blockedBy.length > 0
        || this.blocking.length > 0
        || this.relates.length > 0;
    },
  },

  methods: {
    rowId(row) {
      return templateIdString(row?._id) || templateIdString(row?.taskId) || '';
    },

    rowLabel(row) {
      return truncateDependencyDescription(row?.description) || 'Task';
    },

    rowTitle(row) {
      return row?.description || 'Task';
    },

    metaLine(row) {
      const location = (row?.locationName || '').trim() || '—';
      if (!row?.canVisit) {
        return `${location}  -  ${this.copy.DETAIL_STATUS_NOT_VISIBLE}`;
      }
      const status = (row?.status || '').trim();
      return status ? `${location}  -  ${status}` : location;
    },

    onRowClick(row) {
      if (!row?.canVisit) return;
      const id = this.rowId(row);
      if (!id) return;
      const projectId = this.$route.params.projectId;
      if (!projectId) return;
      this.$router
        .push({
          path: `/financialclose/project/checklist/${projectId}/task/${id}`,
          query: { ...(this.$route.query || {}) },
        })
        .catch((err) => {
          if (err?.name !== 'NavigationDuplicated') {
            console.error('[CycleTaskDetailDependencies]:', err);
          }
        });
    },
  },
};
</script>
